import { isObject, isOptional } from "generic-type-guard";
import { call, put, race, SagaGenerator, select, takeLatest, takeMaybe } from "typed-redux-saga";
import { Action } from "typescript-fsa";

import { ActivityActions, ActivityMap, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { Model, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { FormActivity } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentServiceFactory, GroupInstance } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { WorkflowsActions } from "@com.mgmtp.a12.workflows/workflows-core/lib/actions";

import { createCustomNotificationAction } from "../../app/actions";
import { RESOURCE_KEYS } from "../../localization";
import { assertNonNullable, assertTrue } from "../../utilities/assert";
import { validateLockProcess } from "../../utilities/lockProcessUtils";
import { entityInstancePathFromModelPath } from "../../utilities/pathHelpers";
import { getDocument } from "../../utilities/rpcHelper";
import { PATH_TO_ASSIGNEE_GROUP } from "../../utilities/variables/documentModelPaths";

import { ANTRAG_DETAIL_ACTIVITY_ID, TASK_DETAIL_ACTIVITY_ID } from "../processOverview/store/actions";
import { isDocumentWithProcessMetadata } from "../workflows/data/processMetadata";

import { assignProcessDocument, AssignProcessDocumentPayload } from "./actions";

import setData = ActivityActions.setData;
import DocumentAndValidationModel = Model.DocumentAndValidationModel;

const documentService = new DocumentServiceFactory().getDocumentService();

export function* assignProcessSaga(): SagaGenerator<void> {
	return yield* takeLatest(assignProcessDocument, handleAssignDocument);
}

function* handleAssignDocument(action: Action<AssignProcessDocumentPayload>): SagaGenerator<void> {
	yield* put(ApplicationActions.setBusy(true));

	const { docRef, assignee, assigneeHtmlRef } = action.payload;
	const document = (yield* call(getDocument, docRef))?.document;
	if (!isDocumentWithProcessMetadata(document)) {
		yield* put(ApplicationActions.setBusy(false));
		return;
	}

	const lockValidationResult = yield* call(validateLockProcess, docRef, action.payload.activityId);
	if (!lockValidationResult) {
		return;
	}

	if (document.root.processMetadata.archival?.archivalInProgress) {
		yield* put(
			createCustomNotificationAction({
				title: { key: RESOURCE_KEYS.wf.notification.error.assigntask_archival_title },
				message: { key: RESOURCE_KEYS.wf.notification.error.assigntask_archival },
				icon: "error",
				severity: "error",
				activityId: action.payload.activityId,
				group: `claimProcessDocument:${docRef}`
			})
		);
		yield* put(ApplicationActions.setBusy(false));
		return;
	}

	yield* put(WorkflowsActions.assignTask.started({ docRef, assignee: assignee ?? "" }));
	const { done } = yield* race({
		done: takeMaybe(WorkflowsActions.assignTask.done.match),
		failed: takeMaybe(WorkflowsActions.assignTask.failed.match)
	});

	if (!done) {
		return;
	}

	const activityId = action.payload.activityId;
	if (activityId === ANTRAG_DETAIL_ACTIVITY_ID || activityId === TASK_DETAIL_ACTIVITY_ID) {
		const documentModel = yield* select(
			ModelSelectors.modelInScene(
				{ activityId: activityId, modelType: "document" },
				Model.isDocumentAndValidationModel
			)
		);

		if (!documentModel) {
			return;
		}

		const activities = ActivityMap.toList(yield* select(ActivitySelectors.activities()));
		const processActivities = activities.filter(it => it.descriptor.instance === docRef);
		for (const processActivity of processActivities) {
			yield* call(setAssigneeInProcessActivity, processActivity.id, action.payload.assignee ?? "", documentModel);
		}
	} else {
		yield* put(ActivityActions.reloadData({ activityId }));
		yield* takeMaybe(ActivityActions.setData);
	}
	assigneeHtmlRef?.focus();
}

function* setAssigneeInProcessActivity(
	activityId: string,
	assignee: string,
	documentModel: DocumentAndValidationModel
): SagaGenerator<void> {
	const data = yield* select(ActivitySelectors.data(activityId));
	if (data && FormActivity.Data.SingleDocumentData.isInstance(data)) {
		const persistedData = yield* call(getDocument, data.document.id);

		assertNonNullable(persistedData?.document);
		const parsedDocument = documentService.parseDates(persistedData.document, documentModel) as GroupInstance;
		const updatedAssigneeGroupInstance = documentService.getAssignedObject(
			parsedDocument,
			entityInstancePathFromModelPath(PATH_TO_ASSIGNEE_GROUP)
		);
		assertTrue(isOptional(isObject)(updatedAssigneeGroupInstance));

		// Sanity check
		assertTrue(
			!updatedAssigneeGroupInstance ||
				("id" in updatedAssigneeGroupInstance && updatedAssigneeGroupInstance["id"] === assignee)
		);

		const updatedDocument = documentService.updateEntityInstance(
			data.document,
			entityInstancePathFromModelPath(PATH_TO_ASSIGNEE_GROUP),
			updatedAssigneeGroupInstance,
			documentModel
		);

		yield* put(setData({ activityId, data: { ...data, document: updatedDocument } }));
	}
}

export const assignmentSagas = [assignProcessSaga];
